import React from "react";
import Link from "gatsby-link";
import Img from "gatsby-image";
import styled, { keyframes } from "styled-components";
import Footer from "../Footer/Footer"

import Bagger from "../../../static/bagger.svg";

const wrapperShowUp = keyframes`
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
`;

const Wrapper = styled.nav`
  display: ${props =>
    props.isAside ? (props.inTransition ? "block" : "none") : "block"};
  bottom: 0;
  background-color: ${props => props.theme.navigator.backgrounds.wrapper};
  background-image: url(${Bagger});
  background-size: 250%;
  background-position: 50% top;
  background-repeat: no-repeat;
  left: ${props =>
    props.isAside
      ? props => `-${props.theme.navigator.sizes.asideWidth}`
      : "0"};
  padding-top: ${props => props.theme.topBar.sizes.height}px;
  overflow: auto;
  position: absolute;
  top: 0;
  width: ${props =>
    props.isAside ? props.theme.navigator.sizes.asideWidth : "100%"};

  @media screen and (min-width: ${props => props.theme.mediaQueryTresholds.M}) {
    padding-top: ${props =>
      props.isAside
        ? props => props.theme.topBar.sizes.height + 12
        : props => props.theme.topBar.sizes.height + 18}px;
  }
  @media screen and (min-width: ${props => props.theme.mediaQueryTresholds.L}) {
    display: block;
    left: 0;
    transition: all 0.6s ease;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.navigator.colors.scrollTrack};
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.navigator.colors.scrollThumb};
  }

  a {
    display: block;
    padding: ${props => (props.isAside ? "1em 1em 1em .8em" : "2em 1.5em")};
    color: inherit;
    text-decoration: none;
    &:hover {
      color: inherit;
    }
  }
`;

const headerShowUp = keyframes`
  0% {
    opacity: 0;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const Header = styled.header`
  display: block;
  position: ${props => (props.isAside ? "absolute" : "relative")};
  top: ${props => (props.isAside ? "0" : "auto")};
  left: ${props => (props.isAside ? "0" : "auto")};
  font-weight: ${props => (props.isAside ? "300" : "700")};
  color: ${props => (props.isAside ? props.theme.navigator.colors.header : props.theme.navigator.colors.headerIndex)};
  text-transform: ${props => (props.isAside ? "uppercase" : "none")};
  ${props => (props.isAside ? "margin: 1.4em 0 2rem 1.3em;" : "margin: 0 auto 2rem;")}
  max-width: 100%;
  letter-spacing: ${props => (props.isAside ? "0.1em" : "0")};
  font-size: ${props => (props.isAside ? "0.9em" : "2rem")};
  text-align: center;
  padding: ${props => (props.isAside ? "0" : "4rem 2rem")};
  ${props => (!props.isAside ? "background: rgba(255,255,255,.1);" : "")}
  @media screen and (min-width: 480px) {
    font-size: ${props => (props.isAside ? "0.9em" : "2.5rem")};
    padding: ${props => (props.isAside ? "0" : "4rem 6rem")};
  }
  @media screen and (min-width: 784px) {
    font-size: ${props => (props.isAside ? "0.9em" : "3rem")};
    padding: ${props => (props.isAside ? "0" : "4rem 10rem")};
  }
`;

const List = styled.ul`
  display: ${props => (props.inTransition ? "none" : "block")};
  list-style: none;
  max-width: ${props => props.theme.navigator.sizes.maxWidth};
  margin: 0;
  margin-left: auto;
  margin-right: auto;
  padding: 0;
  padding-bottom: ${props => (props.isAside ? "0" : "20vh")};
  overflow: hidden;

  @media screen and (max-width: 450px) {
    padding-bottom: 0;
  }

  @media screen and (min-width: ${props => props.theme.mediaQueryTresholds.S}) {
    padding: ${props => (!props.isAside ? "0 1.5em" : "")};
  }
  @media screen and (min-width: ${props => props.theme.mediaQueryTresholds.M}) {
    padding: ${props => (!props.isAside ? "0 2em" : "")};
  }
  @media screen and (min-width: ${props => props.theme.mediaQueryTresholds.L}) {
    padding: ${props => (!props.isAside ? "0 3em" : "")};
  }
  @media screen and (min-width: ${props =>
      props.theme.mediaQueryTresholds.XL}) {
    padding: ${props => (!props.isAside ? "0" : "")};
  }
`;

const itemShowUp = keyframes`
from {
  opacity: 0;
}
to {
  opacity: 1;
}
`;

const ListItem = styled.li`
  animation-name: ${itemShowUp};
  animation-duration: 0.5s;
  background: ${props =>
    props.isActive
      ? props => props.theme.navigator.backgrounds.asideItemActive
      : "none"};
  border-left: 0.4em solid transparent;
  transition: background 0.5s;
  &:after {
    content: '';
    display: block;
    height: 6rem;
    width: 3px;
    background: ${props => props.theme.navigator.colors.title};
    margin: 0 auto;
    @media screen and (min-width: 784px) {
      ${props => (!props.isAside ? "display: none;" : "")}
    }
  }
  &:last-of-type::after {
    height: 50vh;
  }
  @media screen and (min-width: 784px) {
    ${props => (!props.isAside ? "margin-bottom: 8rem;" : "")}
  }
`;

const Post = styled.div`
  position: relative;
  perspective: 1000px;
  display: flex;
  flex-direction: column;
  @media screen and (min-width: 784px) {
    flex-direction: ${props => (!props.isAside ? "row" : "column")};
  }
`;

const Date = styled.p`
  color: ${props => props.theme.navigator.colors.title};
  font-weight: ${props => (props.isAside ? "400" : "700")};
  font-size: ${props => (props.isAside ? ".95em" : "1rem")};
  letter-spacing: 0;
  line-height: ${props => (props.isAside ? "1.15" : "1.1")};
  text-align: center;
  margin: 0;
  opacity: ${props => (props.inTransition ? "0" : "1")};
  transition: all 0.5s;
  background: ${props => (props.theme.navigator.colors.title)};
  color: ${props => (props.theme.navigator.backgrounds.wrapper)};
  padding: .5rem .75rem;
  border-radius: .5rem;
  margin: 1rem 0;
  @media screen and (min-width: 784px) {
    margin: ${props => (props.isAside ? "1rem 0" : "0 0 1rem")};
    ${props => (!props.isAside? "order: -1;" : "")}
  }

  @media screen and (min-width: ${props => props.theme.mediaQueryTresholds.M}) {
    font-size: ${props => (!props.isAside ? "1.2rem" : "")};
  }
  @media screen and (min-width: ${props => props.theme.mediaQueryTresholds.L}) {
    font-size: ${props => (!props.isAside ? "1.4rem" : "")};
  }
  @media screen and (min-width: ${props =>
      props.theme.mediaQueryTresholds.XL}) {
    font-size: ${props => (!props.isAside ? "1.6rem" : "")};
  }

  a:hover & {
    background: ${props => props.theme.navigator.colors.linkHover};
  }
`;

const Title = styled.h2`
  color: ${props => props.theme.navigator.colors.title};
  font-weight: ${props => (props.isAside ? "400" : "700")};
  font-size: ${props => (props.isAside ? ".95em" : "1.3em")};
  letter-spacing: 0;
  line-height: ${props => (props.isAside ? "1.15" : "1.1")};
  margin: 1rem 0 0;
  opacity: ${props => (props.inTransition ? "0" : "1")};
  transition: all 0.5s;
  @media screen and (min-width: 784px) {
    margin: ${props => (props.isAside ? "1em 0 0" : "0")};
  }

  @media screen and (min-width: ${props => props.theme.mediaQueryTresholds.M}) {
    font-size: ${props => (!props.isAside ? "1.7em" : "")};
  }
  @media screen and (min-width: ${props => props.theme.mediaQueryTresholds.L}) {
    font-size: ${props => (!props.isAside ? "2em" : "")};
  }
  @media screen and (min-width: ${props =>
      props.theme.mediaQueryTresholds.XL}) {
    font-size: ${props => (!props.isAside ? "2.2em" : "")};
  }

  a:hover & {
    color: ${props => props.theme.navigator.colors.linkHover};
  }
`;

const Excerpt = styled.p`
  display: ${props => (props.isAside ? "none" : "block")};
  color: ${props => props.theme.navigator.colors.title};
  font-weight: 400;
  font-size: .8em;
  letter-spacing: 0;
  line-height: 1.4em;
  margin: 1rem 0 0;
  opacity: ${props => (props.inTransition ? "0" : "1")};
  transition: all 0.5s;

  @media screen and (min-width: ${props => props.theme.mediaQueryTresholds.M}) {
    font-size: ${props => (!props.isAside ? "1em" : "")};
  }
  @media screen and (min-width: ${props =>
      props.theme.mediaQueryTresholds.XL}) {
    font-size: ${props => (!props.isAside ? "1.2em" : "")};
  }

  a:hover & {
    color: ${props => props.theme.navigator.colors.linkHover};
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;

  @media screen and (min-width: 784px) {
    ${props => (!props.isAside ? "padding-left: 5%;" : "")}
  }

  &.odd {
    text-align: center;
    .date {
      align-self: center;
    }
    .point {
      display: "block";
    }

    @media screen and (min-width: 784px) {
      text-align: ${props => (props.isAside ? "center" : "right")};
      ${props => (!props.isAside ? "flex: 1 0 45%;" : null)}
      ${props => (!props.isAside ? "max-width: 45%;" : null)}
      ${props => (!props.isAside ? "margin-right: 5%;" : null)}
      ${props => (!props.isAside ? "order: 0" : "")};
      .date {
        align-self: ${props => (props.isAside ? "center" : "flex-end")};
      }
      .point {
        display: ${props => (props.isAside ? "block" : "none")};
      }
    }
  }

  &.even {
    text-align: center;
    .date {
      align-self: center;
    }
    .point {
      background: #fff;
    }

    @media screen and (min-width: 784px) {
      order: ${props => (!props.isAside ? "1" : "")};
      text-align: ${props => (props.isAside ? "center" : "left")};
      .date {
        align-self: ${props => (props.isAside ? "center" : "flex-start")};
      }
      .point {
        position: ${props => (props.isAside ? "relative" : "absolute")};
        top: ${props => (props.isAside ? "auto" : ".25rem")};
        left: ${props => (props.isAside ? "auto" : "-.75rem")};
        background: #fff;
        height: ${props => (props.isAside ? "1.5rem" : "2rem")};
        width: ${props => (props.isAside ? "1.5rem" : "2rem")};
      }
    }
  }
  &.even:before {
    content: '';
    display: inline-block;
    height: 300vh;
    width: .5rem;
    background: ${props => props.theme.navigator.colors.title};
    position: absolute;
    left: 0;
    top: 0;
    display: none;
    @media screen and (min-width: 784px) {
      ${props => (props.isAside ? "display: none;" : "display: block;")}
    }
  }
`;

const Point = styled.div`
  height: 1.5rem;
  width: 1.5rem;
  background: ${props => props.theme.navigator.colors.title};
  border-radius: 50%;
  align-self: center;

  a:hover & {
    background: ${props => props.theme.navigator.colors.linkHover};
  }
`;

const Picture = styled.div`
  @media screen and (max-width: 784px) {
    max-height: 20rem;
    overflow: hidden;
    border-radius: .5rem;
  }
  img {
    box-shadow: ${props => (!props.isAside ? "0 2px 1px rgba(0,0,0,.2)" : "")};
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    border-radius: .5rem;
    max-height: 100%;

    @media (min-width: ${props => props.theme.mediaQueryTresholds.XL}) {
      a:hover & {
        ${props =>
          !props.isAside
            ? `
        box-shadow: 0 20px 10px -10px rgba(0,0,0,.2);
        height: 150%;`
            : ``};
      }
    }
  }
  .point {
    display: none;
  }

  &.even {
    @media screen and (min-width: 784px) {
      ${props => (!props.isAside ? "flex: 1 0 45%;" : null)}
      ${props => (!props.isAside ? "max-width: 45%;" : null)}
      ${props => (!props.isAside ? "margin-right: 5%;" : null)}
      ${props => (!props.isAside ? "order: 0" : "")};
    }
  }

  &.odd {
    .point {
      display: none;
      background: #fff;
    }

    @media screen and (min-width: 784px) {
      ${props => (!props.isAside ? "flex: 1;" : null)}
      ${props => (!props.isAside ? "position: relative;" : null)}
      ${props => (!props.isAside ? "padding-left: 5%;" : null)}
      ${props => (!props.isAside ? "overflow: visible;" : null)}
      ${props => (!props.isAside ? "order: 1" : "")};
      .point {
        position: ${props => (!props.isAside ? "absolute" : "")};
        top: ${props => (!props.isAside ? ".25rem" : "")};
        left: ${props => (!props.isAside ? "-.75rem" : "")};
        height: ${props => (!props.isAside ? "2rem" : "")};
        width: ${props => (!props.isAside ? "2rem" : "")};
        display: ${props => (!props.isAside ? "block" : "")};
      }
    }
  }
  
  .gatsby-image-outer-wrapper {
    height: 100%;
  }
  .gatsby-image-wrapper {
    height: 100%;
  }
`;

class PostsNavigator extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isAside: false
    };
  }

  componentDidMount() {}

  render() {
    return (
      <Wrapper
        isAside={this.props.isAside}
        inTransition={this.props.inTransition}
        isHidden={this.props.isHidden}
        welcomeIsRolledUp={this.props.welcomeIsRolledUp}
      >
        <Header isAside={this.props.isAside}>Neubau/Anbau<br /> für die Ganztagsbetreuung</Header>
        <List
          isAside={this.props.isAside}
          inTransition={this.props.inTransition}
        >
          {this.props.posts &&
            this.props.posts.map((post, index) => {
              const title = post.node.frontmatter.title || post.node.path;
              let even = index % 2 === 0 ? true : false;
              return (
                <ListItem
                  isActive={post.node.frontmatter.path === this.props.location}
                  isAside={this.props.isAside}
                  inTransition={this.props.inTransition}
                  key={post.node.frontmatter.path}
                >
                  <Link
                    to={post.node.frontmatter.path}
                    onClick={this.props.linkOnClick}
                    style={{ padding: "0 1.5em" }}
                  >
                    <Post isAside={this.props.isAside}>
                      <Picture isAside={this.props.isAside} className={even ? "even" : "odd"}>
                        <Img
                          sizes={post.node.frontmatter.cover.children[0].sizes}
                        />
                        <Point isAside={this.props.isAside} className="point"></Point>
                      </Picture>

                      <Content isAside={this.props.isAside} className={even ? "even" : "odd"}>
                        <Title isAside={this.props.isAside}>
                          {post.node.frontmatter.title}
                        </Title>

                        <Excerpt isAside={this.props.isAside}>
                          {post.node.frontmatter.excerpt}
                        </Excerpt>

                        <Date isAside={this.props.isAside} className="date">
                          {post.node.frontmatter.date}
                        </Date>

                        <Point isAside={this.props.isAside} className="point"></Point>
                      </Content>
                    </Post>
                  </Link>
                </ListItem>
              );
            })}
        </List>
        <Footer isAside={this.props.isAside} />
      </Wrapper>
    );
  }
}

export default PostsNavigator;



// WEBPACK FOOTER //
// ./src/components/Navigator/PostsNavigator.jsx